<script lang="ts" setup>
import { segmentation } from '@thecompaniesapi/shared/client'

const { t } = useI18n()
const api = useApi()

const { data: response, execute, status } = useAsyncData(
  'doc:companies-fetch-context',
  () => api.companies.fetchContext(segmentation.freeQueries.domain),
  { immediate: false },
)

const endpoints: ApiEndpoint[] = [
  {
    method: 'GET',
    path: '/companies/:domain/context',
  },
]

const queryAttributes: ApiAttribute[] = [
  {
    description: t('attributes.domain'),
    example: `…/companies/${segmentation.freeQueries.domain}/context`,
    name: 'domain',
    required: true,
    type: 'string',
  },
]

const responseAttributes: ApiAttribute[] = [
  {
    description: t('response.context'),
    name: 'context',
    type: 'object',
  },
  {
    attributes: [
      {
        description: t('response.meta.cost'),
        name: 'cost',
        type: 'number',
      },
      {
        description: t('response.meta.tokens'),
        name: 'tokens',
        type: 'number',
      },
    ],
    description: t('response.meta.description'),
    name: 'meta',
    type: 'object',
  },
]

async function onVisibilityChange(visibility: boolean) {
  if (visibility && !response.value) {
    execute()
  }
}
</script>

<template>
  <ApiDoc
    :endpoints="endpoints"
    :loading="status === 'pending'"
    :query-attributes="queryAttributes"
    :response="response?.data"
    :response-attributes="responseAttributes"
    @visibility-change="onVisibilityChange"
  />
</template>

<i18n lang="json">
{
  "en": {
    "attributes": {
      "domain": "The domain of the company to fetch the context for."
    },
    "response": {
      "context": "The context of the company.",
      "meta": {
        "cost": "The cost of the request.",
        "description": "The meta information about the response.",
        "tokens": "The count of tokens in the context."
      }
    }
  },
  "es": {
    "attributes": {
      "domain": "El dominio de la empresa para la que se desea recuperar el contexto."
    },
    "response": {
      "context": "El contexto de la empresa.",
      "meta": {
        "cost": "El coste de la solicitud.",
        "description": "La información meta sobre la respuesta.",
        "tokens": "El número de tokens en el contexto."
      }
    }
  },
  "fr": {
    "attributes": {
      "domain": "Le domaine de l'entreprise pour laquelle vous souhaitez récupérer le contexte."
    },
    "response": {
      "context": "Le contexte de l'entreprise.",
      "meta": {
        "cost": "Le coût de la requête.",
        "description": "Les informations méta sur la réponse.",
        "tokens": "Le nombre de tokens dans le contexte."
      }
    }
  }
}
</i18n>
